import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FileText, Download, Eye, RefreshCw, Inbox, CheckCircle2, AlertTriangle, Clock, Loader2, ShieldAlert,
} from "lucide-react";

const istFmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      })
    : "—";

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const STATUS_BADGE = {
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  duplicate: "bg-amber-100 text-amber-700 border-amber-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  processing: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function MilestonePdf() {
  const [data, setData] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([api.get("/milestone-pdf/reports"), api.get("/milestone-pdf/deliveries")]);
      setData(r.data);
      setDeliveries(d.data.deliveries || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPdf = async (r, mode) => {
    setBusyId(`${mode}-${r.id}`);
    try {
      const res = await api.get(`/milestone-pdf/reports/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (mode === "view") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = r.original_filename || "milestone-report.pdf";
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const latest = data?.latest;
  const wd = data?.watchdog;
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (loading) {
    return (
      <div className="py-24 flex justify-center" data-testid="milestone-pdf-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="milestone-pdf-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-brand" /> Milestone PDF
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Daily Milestone report received automatically by email via CloudMailin.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="milestone-pdf-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-lg border bg-white p-4" data-testid="milestone-pdf-latest-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Latest received
          </p>
          {latest ? (
            <>
              <p className="mt-2 text-sm font-semibold text-slate-900 truncate">{latest.original_filename}</p>
              <p className="text-xs text-slate-500 mt-0.5">{istFmt(latest.received_at)} IST · {kb(latest.size)}</p>
            </>
          ) : (
            <p className="mt-2 text-sm text-slate-500">No report received yet</p>
          )}
        </div>
        <div className="rounded-lg border bg-white p-4" data-testid="milestone-pdf-today-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Today's status</p>
          {data?.received_today ? (
            <p className="mt-2 text-sm font-semibold text-emerald-700 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> Report received today
            </p>
          ) : (
            <p className="mt-2 text-sm font-semibold text-amber-700 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> Not received yet today
            </p>
          )}
          <p className="text-xs text-slate-500 mt-1">{data?.total || 0} report{(data?.total || 0) === 1 ? "" : "s"} stored in total</p>
        </div>
        <div className="rounded-lg border bg-white p-4" data-testid="milestone-pdf-watchdog-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5" /> Missing-report watchdog
          </p>
          <p className="mt-2 text-sm text-slate-700">
            Alerts super admin at{" "}
            <span className="font-semibold">
              {String(wd?.cutoff_hour ?? 22).padStart(2, "0")}:{String(wd?.cutoff_minute ?? 0).padStart(2, "0")} IST
            </span>{" "}
            if no PDF arrives
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Working days: {(wd?.working_days || []).map((d) => dayNames[d]).join(", ") || "—"}
          </p>
        </div>
      </div>

      <Tabs defaultValue="reports">
        <TabsList data-testid="milestone-pdf-tabs">
          <TabsTrigger value="reports" data-testid="milestone-pdf-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="milestone-pdf-tab-deliveries">Inbound Deliveries</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="mt-4">
          {!data?.reports?.length ? (
            <div className="rounded-lg border bg-white p-10 text-center" data-testid="milestone-pdf-empty">
              <Inbox className="h-8 w-8 mx-auto text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-600">No reports received yet</p>
              <p className="text-xs text-slate-400 mt-1">
                Reports will appear here automatically once the Milestone email lands in CloudMailin.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border bg-white divide-y" data-testid="milestone-pdf-report-list">
              {data.reports.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`milestone-pdf-report-${r.id}`}>
                  <div className="h-9 w-9 rounded-md bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <FileText className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{r.original_filename}</p>
                    <p className="text-xs text-slate-500 truncate">
                      Received {istFmt(r.received_at)} IST · {kb(r.size)}
                      {r.subject ? ` · ${r.subject}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openPdf(r, "view")}
                      disabled={busyId === `view-${r.id}`} data-testid={`milestone-pdf-view-${r.id}`}>
                      {busyId === `view-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      <span className="ml-1.5 hidden sm:inline">View</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openPdf(r, "download")}
                      disabled={busyId === `download-${r.id}`} data-testid={`milestone-pdf-download-${r.id}`}>
                      {busyId === `download-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      <span className="ml-1.5 hidden sm:inline">Download</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          {!deliveries.length ? (
            <div className="rounded-lg border bg-white p-10 text-center" data-testid="milestone-pdf-deliveries-empty">
              <Inbox className="h-8 w-8 mx-auto text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-600">No inbound deliveries logged yet</p>
            </div>
          ) : (
            <div className="rounded-lg border bg-white overflow-x-auto" data-testid="milestone-pdf-delivery-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5">Received (IST)</th>
                    <th className="px-4 py-2.5">From</th>
                    <th className="px-4 py-2.5">Subject</th>
                    <th className="px-4 py-2.5">Attachments</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {deliveries.map((d) => (
                    <tr key={d.id} data-testid={`milestone-pdf-delivery-${d.id}`}>
                      <td className="px-4 py-2.5 whitespace-nowrap text-slate-700">{istFmt(d.at)}</td>
                      <td className="px-4 py-2.5 max-w-[220px] truncate text-slate-700">{d.from_addr || "—"}</td>
                      <td className="px-4 py-2.5 max-w-[260px] truncate text-slate-600">{d.subject || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-600">{d.attachment_count ?? 0}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={STATUS_BADGE[d.status] || STATUS_BADGE.processing}>
                          {d.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 max-w-[220px] truncate text-slate-500">{d.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
